import { makeAutoObservable } from 'mobx';
import { RootStore } from './RootStore';
import { AIPrompt, ClipProducerPreset } from '@src/types.ts/ai_prompts';
import { TemplatedPromptsRepository } from '@src/repositories';

export class TemplatedPromptsStore {
  private rootStore: RootStore;
  private readonly _customClipProducerPreset: ClipProducerPreset = {
    title: 'Custom',
    category: 'clip_producer_custom',
  };
  private _clipProducerPresets: ClipProducerPreset[] = [];
  private _aiBlogTemplates: AIPrompt[] = [];
  private _aiSocialPostTemplates: AIPrompt[] = [];
  private _aiEmailTemplates: AIPrompt[] = [];
  private _aiShareableImagesTemplates: AIPrompt[] = [];
  private _aiTalkingPointsTemplate: AIPrompt | null = null;

  private get repository(): TemplatedPromptsRepository {
    if (!this.rootStore.datoClientStore) {
      throw new Error('Templated prompts repository not initialized');
    }
    return this.rootStore.datoClientStore.templatedPromptsRepository;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get customClipProducerPreset(): ClipProducerPreset {
    return this._customClipProducerPreset;
  }

  get clipProducerPresets(): ClipProducerPreset[] {
    return this._clipProducerPresets;
  }

  get aiBlogTemplates(): AIPrompt[] {
    return this._aiBlogTemplates;
  }

  get aiSocialPostTemplates(): AIPrompt[] {
    return this._aiSocialPostTemplates;
  }

  get aiEmailTemplates(): AIPrompt[] {
    return this._aiEmailTemplates;
  }

  get aiShareableImagesTemplates(): AIPrompt[] {
    return this._aiShareableImagesTemplates;
  }

  get aiTalkingPointsTemplate(): AIPrompt | null {
    return this._aiTalkingPointsTemplate;
  }

  private setClipProducerPresets(presets: ClipProducerPreset[]) {
    this._clipProducerPresets = presets;
  }

  private setAiBlogTemplates(templates: AIPrompt[]) {
    this._aiBlogTemplates = templates;
  }

  private setAiSocialPostTemplates(templates: AIPrompt[]) {
    this._aiSocialPostTemplates = templates;
  }

  private setAiEmailTemplates(templates: AIPrompt[]) {
    this._aiEmailTemplates = templates;
  }

  private setAiShareableImagesTemplates(templates: AIPrompt[]) {
    this._aiShareableImagesTemplates = templates;
  }

  private setAiTalkingPointsTemplate(template: AIPrompt | null) {
    this._aiTalkingPointsTemplate = template;
  }

  async initTemplatedPrompts(storyId: string) {
    const {
      clipProducerPresets,
      aiBlogTemplates,
      aiSocialPostTemplates,
      aiEmailTemplates,
      aiShareableImagesTemplates,
      aiTalkingPointsTemplate,
    } = await this.repository.fetchTemplatedPrompts(storyId);

    this.setClipProducerPresets(clipProducerPresets);
    this.setAiBlogTemplates(aiBlogTemplates);
    this.setAiSocialPostTemplates(aiSocialPostTemplates);
    this.setAiEmailTemplates(aiEmailTemplates);
    this.setAiShareableImagesTemplates(aiShareableImagesTemplates);
    this.setAiTalkingPointsTemplate(aiTalkingPointsTemplate);
  }
}
